import { debounce } from "lodash";
import {
  ChangeEvent,
  ChangeEventHandler,
  ComponentType,
  FocusEventHandler,
  ReactNode,
  useCallback,
  useState,
} from "react";
import styled from "styled-components";

import { InsurancePlan } from "pwapi";

import AdvancedLookupForm from "./AdvancedLookupForm";
import { InsuranceListItem } from "./AsyncListItems";
import { locationOptions } from "../utils/usRegions";

export type Option = {
  label: string;
  value: string;
  disabled?: boolean;
};

type SelectInputProps = {
  defaultLabel?: string;
  disableDefault?: boolean;
  disabled?: boolean;
  options: Option[];
  value: string;
  onChange: ChangeEventHandler<HTMLSelectElement>;
  onBlur?: FocusEventHandler<HTMLElement>;
  label?: string;
  id?: string;
  name?: string;
  error?: string;
  required?: boolean;
  skipBlankOption?: boolean;
};

type TextInputProps = {
  value: string;
  placeholder?: string;
  pattern?: string;
  onChange: any;
  onClear?: any;
  type?: "text" | "email" | "password" | "number" | "tel";
  onBlur?: FocusEventHandler<HTMLElement>;
  label?: string;
  id?: string;
  name?: string;
  autoComplete?: "on" | "off" | "new-password";
  error?: string;
  isClearable?: boolean;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  button?: ReactNode;
  maxLength?: number;
  minLength?: number;
  spellCheck?: boolean;
  leadingContent?: ReactNode;
};

const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;

  align-self: start;
`;

const InputGroup = styled.div<{ $isSpread?: boolean }>`
  display: flex;
  gap: ${(props) => (props.$isSpread ? "0.5em" : "0")};

  > div {
    flex: 1;
  }
`;

const AdvancedInsurancePlanLookup = ({
  SelectInput,
  TextInput,
  selectedPlan,
  apiOperation,
  onSelectItem,
  spreadInputs,
}: {
  SelectInput: ComponentType<SelectInputProps>;
  TextInput: ComponentType<TextInputProps>;
  selectedPlan?: InsurancePlan;
  apiOperation: any;
  onSelectItem: Function;
  spreadInputs?: boolean;
}) => {
  const [fields, setFields] = useState<{
    payerName?: string;
    payerLocationStreet?: string;
    payerLocationStreet2?: string;
    payerLocationCity?: string;
    payerLocationState?: string;
    payerLocationZipcode?: string;
  }>({
    payerName: "",
    payerLocationStreet: "",
    payerLocationStreet2: "",
    payerLocationCity: "",
    payerLocationState: "",
    payerLocationZipcode: "",
  });

  const [selections, setSelections] = useState<InsurancePlan[]>([]);
  const [loadingSelections, setIsLoadingSelections] = useState<boolean>(false);

  const checkFields = (fields: { [key: string]: string }) => {
    return !!Object.values(fields).find((value) => value);
  };

  const cleanFields = (fields: { [key: string]: string }) => {
    return Object.keys(fields).reduce(
      (acc, key) => (fields[key] ? { ...acc, [key]: fields[key] } : acc),
      {}
    );
  };

  const onChangeInput = (fieldKey: string, fieldValue: string) => {
    onHandleChange(fieldKey, fieldValue);
    const newFields = { ...fields, [fieldKey]: fieldValue };
    if (checkFields(newFields)) {
      setIsLoadingSelections(true);
      onFetchSuggestions(newFields);
    }
  };

  const fetchInsurances = async (values: { [key: string]: string }) => {
    const responseCodes = await apiOperation(values).finally(() =>
      setIsLoadingSelections(false)
    );
    setSelections(responseCodes.results || []);
  };

  const onFetchSuggestions = useCallback(
    debounce(
      (values) => {
        fetchInsurances({ ...cleanFields(values), limit: "9999" });
      },
      500,
      {}
    ),
    []
  );

  const onHandleChange = (field: string, value: string) => {
    setFields({ ...fields, [field]: value });
  };

  return (
    <AdvancedLookupForm
      selections={
        checkFields(fields) ? (
          loadingSelections ? (
            <span>Loading Selections...</span>
          ) : selections.length ? (
            selections.map((result) => (
              <InsuranceListItem
                object={result}
                key={result.id}
                handleOnClick={onSelectItem}
                isSelected={result.id === selectedPlan?.id}
              />
            ))
          ) : (
            <span>There are no matching insurance plans</span>
          )
        ) : (
          <span>Please search by any field</span>
        )
      }
      inputComponents={
        <InputsContainer>
          <TextInput
            label="Insurance Company"
            id="payerName"
            value={fields.payerName || ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChangeInput("payerName", e.target.value)
            }
          />
          <TextInput
            label="Street Line 1"
            id="payerLocationStreet"
            value={fields.payerLocationStreet || ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChangeInput("payerLocationStreet", e.target.value)
            }
          />
          <TextInput
            label="Street Line 2"
            id="payerLocationStreet2"
            value={fields.payerLocationStreet2 || ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChangeInput("payerLocationStreet2", e.target.value)
            }
          />
          <InputGroup $isSpread={spreadInputs}>
            <TextInput
              label="City"
              id="payerLocationCity"
              value={fields.payerLocationCity || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChangeInput("payerLocationCity", e.target.value)
              }
            />
            <SelectInput
              label="State"
              options={locationOptions}
              id="payerLocationState"
              value={fields.payerLocationState || ""}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                onChangeInput("payerLocationState", e.target.value)
              }
            />
            <TextInput
              label="Zipcode"
              id="payerLocationZipcode"
              value={fields.payerLocationZipcode || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChangeInput("payerLocationZipcode", e.target.value)
              }
            />
          </InputGroup>
        </InputsContainer>
      }
    />
  );
};

export default AdvancedInsurancePlanLookup;
